/**
 * Prayer Times Calculator
 * Primary: Aladhan API (accurate, professional calculation)
 * Fallback: Local ISNA calculation (offline support)
 */

// Cache for API results (key: "lat_lng_date")
const prayerTimesCache = new Map();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

/**
 * Fetch prayer times from Aladhan API
 * @param {Date} date - The date to calculate for
 * @param {number} latitude - Latitude in degrees
 * @param {number} longitude - Longitude in degrees
 * @returns {Promise<Object>} Prayer times as Date objects
 */
export const fetchPrayerTimesFromAPI = async (date, latitude, longitude) => {
  const dateStr = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  const cacheKey = `${latitude.toFixed(2)}_${longitude.toFixed(2)}_${dateStr}`;
  
  // Check cache first
  const cached = prayerTimesCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  try {
    // Aladhan API - Method 2 = ISNA (Islamic Society of North America)
    // Other methods: 1=MWL, 3=Muslim World League, 4=Umm Al-Qura, 5=Egyptian
    const url = `https://api.aladhan.com/v1/timings/${dateStr}?latitude=${latitude}&longitude=${longitude}&method=2`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.code !== 200 || !data.data?.timings) {
      throw new Error('Invalid API response');
    }
    
    const timings = data.data.timings;
    
    // Parse time strings (HH:MM) to Date objects
    const parseTime = (timeStr) => {
      if (!timeStr) return null;
      // Remove timezone suffix like "(CET)" if present
      const cleanTime = timeStr.replace(/\s*\([^)]*\)\s*$/, '').trim();
      const [hours, minutes] = cleanTime.split(':').map(Number);
      const result = new Date(date);
      result.setHours(hours, minutes, 0, 0);
      return result;
    };
    
    const result = {
      fajr: parseTime(timings.Fajr),
      sunrise: parseTime(timings.Sunrise),
      dhuhr: parseTime(timings.Dhuhr),
      asr: parseTime(timings.Asr),
      maghrib: parseTime(timings.Maghrib), // Iftar time
      isha: parseTime(timings.Isha),
      suhoorEnd: null, // Calculate below
      source: 'api'
    };
    
    // Suhoor ends 10 minutes before Fajr
    if (result.fajr) {
      result.suhoorEnd = new Date(result.fajr.getTime() - 10 * 60 * 1000);
    }
    
    // Cache the result
    prayerTimesCache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });
    
    return result;
  } catch (error) {
    console.warn('Aladhan API error, falling back to local calculation:', error.message);
    // Fallback to local calculation
    return calculatePrayerTimesLocal(date, latitude, longitude);
  }
};

// ============================================
// LOCAL CALCULATION (FALLBACK)
// ============================================

const ISNA_FAJR_ANGLE = 15;
const ISNA_ISHA_ANGLE = 15;

const toRadians = (degrees) => (degrees * Math.PI) / 180;
const toDegrees = (radians) => (radians * 180) / Math.PI;

const getDayOfYear = (date) => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
};

const getSunDeclination = (dayOfYear) => {
  const angle = (360 / 365) * (dayOfYear - 81);
  return 23.45 * Math.sin(toRadians(angle));
};

const getEquationOfTime = (dayOfYear) => {
  const B = toRadians((360 / 365) * (dayOfYear - 81));
  return 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
};

const getHourAngle = (latitude, declination, angle = -0.833) => {
  const latRad = toRadians(latitude);
  const decRad = toRadians(declination);
  const angleRad = toRadians(angle);
  
  const cosHA = (Math.sin(angleRad) - Math.sin(latRad) * Math.sin(decRad)) / 
                (Math.cos(latRad) * Math.cos(decRad));
  
  if (cosHA > 1 || cosHA < -1) {
    return null;
  }
  
  return toDegrees(Math.acos(cosHA)) / 15;
};

/**
 * Local prayer times calculation (fallback when API unavailable)
 */
const calculatePrayerTimesLocal = (date, latitude, longitude, timezone = null) => {
  if (timezone === null) {
    timezone = -date.getTimezoneOffset() / 60;
  }
  
  const dayOfYear = getDayOfYear(date);
  const declination = getSunDeclination(dayOfYear);
  const eqTime = getEquationOfTime(dayOfYear);
  
  const solarNoonMinutes = 720 - (longitude * 4) - eqTime + (timezone * 60);
  const solarNoon = solarNoonMinutes / 60;
  
  const sunriseHA = getHourAngle(latitude, declination, -0.833);
  const sunsetHA = sunriseHA;
  const fajrHA = getHourAngle(latitude, declination, -ISNA_FAJR_ANGLE);
  const ishaHA = getHourAngle(latitude, declination, -ISNA_ISHA_ANGLE);
  const asrAngle = toDegrees(Math.atan(1 / (1 + Math.tan(toRadians(Math.abs(latitude - declination))))));
  const asrHA = getHourAngle(latitude, declination, asrAngle);
  
  const createTime = (hours) => {
    if (hours === null || isNaN(hours)) return null;
    
    let h = hours;
    if (h < 0) h += 24;
    if (h >= 24) h -= 24;
    
    const totalMinutes = Math.round(h * 60);
    const hour = Math.floor(totalMinutes / 60) % 24;
    const minute = totalMinutes % 60;
    
    const result = new Date(date);
    result.setHours(hour, minute, 0, 0);
    return result;
  };
  
  const fajr = createTime(solarNoon - fajrHA);
  const suhoorEnd = fajr ? new Date(fajr.getTime() - 10 * 60 * 1000) : null;
  
  return {
    fajr,
    sunrise: createTime(solarNoon - sunriseHA),
    dhuhr: createTime(solarNoon + 0.0167),
    asr: createTime(solarNoon + asrHA),
    maghrib: createTime(solarNoon + sunsetHA),
    isha: createTime(solarNoon + ishaHA),
    suhoorEnd,
    source: 'local'
  };
};

/**
 * Main function to calculate prayer times
 * Uses API first, falls back to local calculation
 */
export const calculatePrayerTimes = async (date, latitude, longitude) => {
  try {
    return await fetchPrayerTimesFromAPI(date, latitude, longitude);
  } catch (error) {
    console.warn('Prayer times API failed, using local calculation');
    return calculatePrayerTimesLocal(date, latitude, longitude);
  }
};

/**
 * Synchronous version for immediate display (uses cache or local calc)
 */
export const calculatePrayerTimesSync = (date, latitude, longitude) => {
  const dateStr = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  const cacheKey = `${latitude.toFixed(2)}_${longitude.toFixed(2)}_${dateStr}`;
  
  const cached = prayerTimesCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  // Return local calculation immediately, API will update async
  return calculatePrayerTimesLocal(date, latitude, longitude);
};

/**
 * Format time as HH:MM
 */
export const formatPrayerTime = (date) => {
  if (!date) return '--:--';
  return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Calculate countdown to a target time
 */
export const getCountdown = (targetTime) => {
  if (!targetTime) return { hours: 0, minutes: 0, seconds: 0, isOver: true };
  
  const now = new Date();
  const diff = targetTime - now;
  
  if (diff <= 0) {
    return { hours: 0, minutes: 0, seconds: 0, isOver: true };
  }
  
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { hours, minutes, seconds, isOver: false };
};

/**
 * Popular cities with coordinates
 */
export const POPULAR_CITIES = {
  // Germany
  berlin: { name: 'Berlin', lat: 52.52, lng: 13.405, country: 'DE' },
  munich: { name: 'München', lat: 48.1351, lng: 11.582, country: 'DE' },
  hamburg: { name: 'Hamburg', lat: 53.5511, lng: 9.9937, country: 'DE' },
  frankfurt: { name: 'Frankfurt', lat: 50.1109, lng: 8.6821, country: 'DE' },
  cologne: { name: 'Köln', lat: 50.9375, lng: 6.9603, country: 'DE' },
  dusseldorf: { name: 'Düsseldorf', lat: 51.2277, lng: 6.7735, country: 'DE' },
  stuttgart: { name: 'Stuttgart', lat: 48.7758, lng: 9.1829, country: 'DE' },
  dortmund: { name: 'Dortmund', lat: 51.5136, lng: 7.4653, country: 'DE' },
  essen: { name: 'Essen', lat: 51.4556, lng: 7.0116, country: 'DE' },
  bremen: { name: 'Bremen', lat: 53.0793, lng: 8.8017, country: 'DE' },
  // Austria
  vienna: { name: 'Wien', lat: 48.2082, lng: 16.3738, country: 'AT' },
  // Switzerland
  zurich: { name: 'Zürich', lat: 47.3769, lng: 8.5417, country: 'CH' },
  // Bosnia
  sarajevo: { name: 'Sarajevo', lat: 43.8563, lng: 18.4131, country: 'BA' },
  // Turkey
  istanbul: { name: 'Istanbul', lat: 41.0082, lng: 28.9784, country: 'TR' },
  ankara: { name: 'Ankara', lat: 39.9334, lng: 32.8597, country: 'TR' },
  // Indonesia
  jakarta: { name: 'Jakarta', lat: -6.2088, lng: 106.8456, country: 'ID' },
  // Middle East
  mecca: { name: 'Mekka', lat: 21.4225, lng: 39.8262, country: 'SA' },
  medina: { name: 'Medina', lat: 24.5247, lng: 39.5692, country: 'SA' },
  dubai: { name: 'Dubai', lat: 25.2048, lng: 55.2708, country: 'AE' },
  cairo: { name: 'Kairo', lat: 30.0444, lng: 31.2357, country: 'EG' },
};

/**
 * Get user's location via Geolocation API
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        reject(error);
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  });
};

/**
 * Check if current time is within Laylatul Qadr period (last 10 nights)
 */
export const isLaylatulQadr = (ramadanDay) => {
  return ramadanDay >= 21 && ramadanDay <= 30;
};

/**
 * Check if it's an odd night (special Laylatul Qadr nights)
 */
export const isOddNight = (ramadanDay) => {
  return [21, 23, 25, 27, 29].includes(ramadanDay);
};
